import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { STATE_LOADING } from '../constants/state.constants';
import { AppState } from '../reducers/root.reducer';
import { AuthService } from '../services/AuthService';

const auth = (state: AppState) => state.auth;

const getCurrentUser = createSelector(auth, (auth) => auth.user);

const getAuthToken = createSelector(getCurrentUser, (user) => user.accessToken);

const getRefreshToken = createSelector(
    getCurrentUser,
    (user) => user.refreshToken,
);

const isLoggedIn = createSelector(getCurrentUser, (user) => user.isLoggedIn);

const getActiveQuestionnaires = createSelector(
    auth,
    (auth) => auth.activeQuestionnaires,
);

const getActiveQuestionnaire = createSelector(
    getActiveQuestionnaires,
    (questionnaires) => {
        if (questionnaires?.length > 0) {
            return questionnaires[0];
        }

        return null;
    },
);

const isLoading = createSelector(
    auth,
    (auth): boolean => auth.loading === STATE_LOADING,
);

const getResetPasswordSuccess = createSelector(
    auth,
    (auth): boolean => auth.resetPasswordSuccess,
);

const getLoginWithTokenStatus = createSelector(auth, (auth) => auth.tokenLogin);

const getLogin = createSelector(auth, (auth) => auth.login);

const isAnonymous = createSelector(getAuthToken, AuthService.isAnonymous);

const getEmail = createSelector(getAuthToken, AuthService.getEmail);
const isAdmin = createSelector(getAuthToken, AuthService.isUserAdmin);

const shouldRedirectToQuestionnaire = createSelector(
    getCurrentUser,
    (user) => user.shouldRedirectToQuestionnaire,
);

const hasLoggedOut = createSelector(
    getCurrentUser,
    (user) => user.hasLoggedOut,
);

function useGetCurrentUser() {
    return useSelector(getCurrentUser);
}

function useGetLogin() {
    return useSelector(getLogin);
}

function useGetEmail() {
    return useSelector(getEmail);
}

function useIsAdmin() {
    return useSelector(isAdmin);
}

function useIsLogged() {
    return useSelector(isLoggedIn);
}

function useGetAuthToken() {
    return useSelector(getAuthToken);
}

const AuthSelectors = {
    getCurrentUser,
    getAuthToken,
    getRefreshToken,
    getActiveQuestionnaires,
    getActiveQuestionnaire,
    isLoggedIn,
    isLoading,
    getResetPasswordSuccess,
    getLoginWithTokenStatus,
    getLogin,
    isAnonymous,
    isAdmin,
    useGetCurrentUser,
    useGetLogin,
    useGetEmail,
    useIsAdmin,
    shouldRedirectToQuestionnaire,
    useIsLogged,
    hasLoggedOut,
    useGetAuthToken,
};

export default AuthSelectors;
